import { getWatchdutyTileUrl } from 'components/Map/layers/getWatchdutyTileUrl';
import {
  MapboxFeature,
  useMapLayerEvents,
} from 'components/Map/layers/useMapLayerEvents';
import VisibilityToggledSource from 'components/Map/layers/VisibilityToggledSource';
import { useCallback, useMemo } from 'react';
import addVisible from 'shared/addVisible';
import { Layer, MapRef, useMapInstance } from 'shared/map-exports';
import { useAuthState } from 'state/useAuthState';
import { useHistory } from 'react-router-dom';
import { useSelectedWeatherStationId } from 'features/weatherStations/hooks/useSelectedWeatherStationId';
import { useWeatherStation } from 'features/weatherStations/hooks/useWeatherStation';
import { LatLng } from 'shared/types';
import { useCenterMap } from 'components/IncidentsMap/CenterMap';
import {
  SOURCE_LAYER,
  WEATHER_STATIONS_ARROW_ICONS_LAYER_ID,
  WEATHER_STATIONS_LABELS_LAYER_ID,
  WEATHER_STATIONS_LABELS_WIND_GUST_LAYER_ID,
  WEATHER_STATIONS_LABELS_WIND_SPEED_LAYER_ID,
  WEATHER_STATIONS_POINT_ICONS_LAYER_ID,
  WEATHER_STATIONS_SELECTED_POINT_ICON_LAYER_ID,
  WEATHER_STATIONS_SOURCE_ID,
} from './WeatherStationsLayer.constants';
import {
  getArrowIconStyle,
  getLabelStyle,
  getPointIconStyle,
  getSelectedPointIconStyle,
  getWindGustLabelStyle,
  getWindSpeedLabelStyle,
} from './WeatherStationsLayer.styles';
import { usePulseAnimation } from './usePulseAnimation';

type WeatherStationsLayerProps = {
  visible: boolean;
};

type WeatherStationsFeatureProperties = {
  air_temp?: number;
  elevation: string;
  is_observation_recent: boolean;
  is_offline: boolean;
  is_within_coverage_area: boolean;
  name: string;
  stid: string;
  weather_station_type: 'atmospheric';
  dew_point_temperature?: number;
  relative_humidity?: number;
  wind_direction?: number;
  wind_gust?: number;
  wind_speed?: number;
};

const REFRESH_INTERVAL_SECONDS = 60 * 15; // 15 minutes

const getFeatureCoordinates = (input: {
  mapInstance?: MapRef;
  sourceId: string;
  featureId: number;
}): LatLng | null => {
  const { mapInstance, sourceId, featureId } = input;

  if (!mapInstance) {
    return null;
  }

  const features = mapInstance.querySourceFeatures(sourceId, {
    sourceLayer: SOURCE_LAYER,
  });

  const feature = features.find((f) => f.id === featureId);

  if (feature?.geometry.type === 'Point') {
    const [lng, lat] = feature.geometry.coordinates;
    return { lng, lat };
  }

  return null;
};

export const WeatherStationsLayer = (
  props: WeatherStationsLayerProps,
): JSX.Element => {
  const { visible } = props;
  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const history = useHistory();
  const activeStationId = useSelectedWeatherStationId();
  const { weatherStation } = useWeatherStation({ id: activeStationId });
  const mapInstance = useMapInstance();

  usePulseAnimation(Boolean(activeStationId), mapInstance?.getMap());

  const handleClick = useCallback(
    (features: MapboxFeature<WeatherStationsFeatureProperties>[]) => {
      const feature = features[0];
      const featureId = feature.id;
      history.push(`/weather_station/${featureId}`);
    },
    [history],
  );

  useMapLayerEvents<WeatherStationsFeatureProperties>({
    layerId: WEATHER_STATIONS_ARROW_ICONS_LAYER_ID,
    onClick: handleClick,
    stopTouchMouseEventPropagation: true,
  });
  useMapLayerEvents<WeatherStationsFeatureProperties>({
    layerId: WEATHER_STATIONS_POINT_ICONS_LAYER_ID,
    onClick: handleClick,
    stopTouchMouseEventPropagation: true,
  });

  const activeStationLatLng = useMemo(() => {
    if (!activeStationId) return null;
    const latLngFromFeature = getFeatureCoordinates({
      mapInstance,
      sourceId: WEATHER_STATIONS_SOURCE_ID,
      featureId: activeStationId,
    });
    if (!latLngFromFeature && weatherStation?.location) {
      return weatherStation.location;
    }
    return latLngFromFeature;
  }, [activeStationId, mapInstance, weatherStation?.location]);

  useCenterMap({
    latLng: activeStationLatLng,
    drawerIsOpen: true,
    defaultSnapPointPct: 0.47,
    zoomIn: true,
  });

  return (
    <VisibilityToggledSource
      id={WEATHER_STATIONS_SOURCE_ID}
      visible={visible}
      url={getWatchdutyTileUrl(SOURCE_LAYER)}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={WEATHER_STATIONS_SELECTED_POINT_ICON_LAYER_ID}
        {...addVisible(
          getSelectedPointIconStyle(isInternalUser, activeStationId),
          visible,
        )}
      />
      <Layer
        id={WEATHER_STATIONS_ARROW_ICONS_LAYER_ID}
        {...addVisible(getArrowIconStyle(isInternalUser), visible)}
      />
      <Layer
        id={WEATHER_STATIONS_POINT_ICONS_LAYER_ID}
        {...addVisible(getPointIconStyle(isInternalUser), visible)}
      />
      <Layer
        id={WEATHER_STATIONS_LABELS_LAYER_ID}
        {...addVisible(getLabelStyle(isInternalUser), visible)}
      />
      <Layer
        id={WEATHER_STATIONS_LABELS_WIND_SPEED_LAYER_ID}
        {...addVisible(getWindSpeedLabelStyle(isInternalUser), visible)}
      />
      <Layer
        id={WEATHER_STATIONS_LABELS_WIND_GUST_LAYER_ID}
        {...addVisible(getWindGustLabelStyle(isInternalUser), visible)}
      />
    </VisibilityToggledSource>
  );
};
